import { H3 } from '@frontend/shadcn/components/typography/h3';
import React, { useMemo } from 'react';
import { faBolt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@frontend/shadcn/components/ui/button';
import { useProjectByIdQuery } from '@frontend/app/hooks';
import { ListCheckIcon, PlusIcon } from '@revfluence/fresh-icons/solid/esm';
import { Empty } from '@frontend/app/refresh-components';
import { Application } from './Components/Application';
import { ApplicationSnapshot } from './Components/ApplicationSnapshot';
import { NewApplicationDrawer } from './Components/NewApplicationDrawer';
import { SectionCard } from '../../CampaignPage/components/SectionCard';
import { DuplicateApplicationDrawer } from './Components/DuplicateApplicationDrawer';

type TProps = {
  projectId: number;
};

const AddApplicationButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Button onClick={onClick} className="flex items-center gap-2 self-start mt-0.5" variant="outline" size="xs">
    <FontAwesomeIcon icon={faPlus} />
    New Application
  </Button>
);

export const AllApplications = (props: TProps) => {
  const { projectId } = props;
  const [isNewApplicationDrawerOpen, setIsNewApplicationDrawerOpen] = React.useState(false);
  const [isDuplicateApplicationDrawerOpen, setIsDuplicateApplicationDrawerOpen] = React.useState(false);

  const { data: { project = undefined } = {}, refetch: refetchProject } = useProjectByIdQuery({
    variables: {
      id: projectId,
    },
    skip: !projectId,
  });

  const applicationsTemplates = useMemo(() => {
    return project?.onboardingTemplates || [];
  }, [project]);

  const onCloseNewApplicationDrawer = () => {
    setIsNewApplicationDrawerOpen(false);
    setIsDuplicateApplicationDrawerOpen(false);
    refetchProject();
  };

  const onCloseDuplicateApplicationDrawer = () => {
    setIsDuplicateApplicationDrawerOpen(false);
    refetchProject();
  };

  const onClickDuplicateApplication = () => {
    setIsNewApplicationDrawerOpen(false);
    setIsDuplicateApplicationDrawerOpen(true);
  };

  return (
    <div className="flex flex-col lg:flex-row h-auto lg:h-full bg-gray-50 overflow-hidden">
      {/* Campaign Snapshot - First on mobile, right on desktop */}
      <div className="w-full lg:w-[35%] lg:order-2 overflow-hidden p-6 flex flex-col gap-2 lg:h-full">
        {/* TODO: Add application summary */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div className="bg-grey-6 rounded-lg p-2 h-6 w-6 flex items-center justify-center">
              <FontAwesomeIcon icon={faBolt} className="text-white" />
            </div>
            <H3 className="font-medium p-0 m-0">Application Page Snapshot</H3>
          </div>
        </div>
        <ApplicationSnapshot />
      </div>

      {/* Divider */}
      <div className="hidden lg:block w-px bg-gray-200 lg:order-2" />

      {/* Left Section - Form - Second on mobile, left on desktop */}
      <div className="w-full lg:w-[65%] lg:order-1 overflow-y-scroll p-6 flex flex-col gap-2 lg:h-full">
        <H3 className="m-0 font-medium text-lg">Recruiting</H3>
        <SectionCard
          title="Application Page"
          description="Customize your application page to generate inbound interest for your campaign and publish it to Aspire's Creator's Marketplace."
          rightContent={<AddApplicationButton onClick={() => setIsNewApplicationDrawerOpen(true)} />}
          className="w-full max-w-[700px]"
        >
          {applicationsTemplates.length === 0 && (
            <Empty
              title="Create an Application Page"
              description="Application Pages enable creators to learn more about your campaign and apply to collaborate."
              icon={ListCheckIcon}
              primaryCtaProps={{
                onClick: () => setIsNewApplicationDrawerOpen(true),
                text: 'New Application',
                icon: PlusIcon,
              }}
              size="lg"
              layout="horizontal"
              withBackground
            />
          )}
          {applicationsTemplates.map((application) => (
            <Application key={application.id} application={application} onUpdate={refetchProject} projectId={projectId} />
          ))}
        </SectionCard>
      </div>
      {isNewApplicationDrawerOpen && (
        <NewApplicationDrawer
          projectId={projectId}
          isOpen={isNewApplicationDrawerOpen}
          onClose={onCloseNewApplicationDrawer}
          onClickDuplicateApplication={onClickDuplicateApplication}
        />
      )}
      {isDuplicateApplicationDrawerOpen && (
        <DuplicateApplicationDrawer
          projectId={projectId}
          isOpen={isDuplicateApplicationDrawerOpen}
          onClose={onCloseDuplicateApplicationDrawer}
        />
      )}
    </div>
  );
};
